import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../../contexts/auth-context'
import {
  approveMyDevice,
  listMyDevices,
  registerMyDevice,
  revokeMyDevice,
  type UserDevice,
} from '../../../services/appsync-client'
import { getDeviceId } from '../../../utils/device-id'
import { StepUpConfirmModal } from '../../security/components/step-up-confirm-modal'
import { useBiometricGuard } from '../../security/hooks/use-biometric-guard'
import { collectRegistrationPayload } from '../hooks/use-device-registry'
import {
  ACTIVE_APPROVER_WINDOW_DAYS,
  canVerifyByEmail,
  formatApproverNames,
  summarizeDeviceApproval,
  type DeviceApprovalSummary,
} from '../utils/device-approval-status'
import { VerifyDeviceEmailDialog } from './verify-device-email-dialog'

const REFRESH_INTERVAL_MS = 60_000
const MESSAGE_TTL_MS = 6_000

/** Dismissal lasts until the app is reloaded / relaunched. */
let dismissedPendingKey: string | null = null

/**
 * In-app device-approval banner, on both sides of the request:
 *  - this install is pending → say so, name the device(s) that can approve it
 *    (only ones used in the last 14 days), or offer email verification when
 *    only idle ones are left, and make clear calls and messages already work;
 *  - this install is trusted and another is pending → offer to approve it.
 */
export function PendingDeviceApprovalBanner() {
  const { user } = useAuth()
  const userId = user?.userId ?? null
  const thisDeviceId = getDeviceId()
  const [summary, setSummary] = useState<DeviceApprovalSummary<UserDevice>>({ kind: 'none' })
  const [busyId, setBusyId] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [dismissedKey, setDismissedKey] = useState(dismissedPendingKey)
  const [verifyOpen, setVerifyOpen] = useState(false)
  const { state: guardState, requireStepUp, retry: retryStepUp, dismiss: dismissStepUp } =
    useBiometricGuard()

  const refresh = useCallback(async (): Promise<DeviceApprovalSummary<UserDevice> | null> => {
    if (!userId) {
      setSummary({ kind: 'none' })
      return null
    }
    try {
      const payload = await listMyDevices()
      let next = summarizeDeviceApproval(payload.devices, thisDeviceId)
      if (next.kind === 'this-pending' && next.approvers.length === 0 && next.idleTrusted.length === 0) {
        // No trusted device at all — re-registering trusts this install (same
        // rule as an account's first device). Idle trusted ones mean email instead.
        await registerMyDevice(await collectRegistrationPayload())
        next = summarizeDeviceApproval((await listMyDevices()).devices, thisDeviceId)
      }
      setSummary(next)
      return next
    } catch {
      /* banner is best-effort */
      return null
    }
  }, [userId, thisDeviceId])

  useEffect(() => {
    void refresh()
    const id = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [refresh])

  useEffect(() => {
    if (!message) return
    const id = window.setTimeout(() => setMessage(null), MESSAGE_TTL_MS)
    return () => window.clearTimeout(id)
  }, [message])

  const onCheckAgain = async () => {
    setChecking(true)
    setError(null)
    setMessage(null)
    const next = await refresh()
    setChecking(false)
    if (!next) {
      setError('Could not check right now. Try again in a moment.')
    } else if (next.kind !== 'this-pending') {
      setMessage('This device is approved.')
    } else {
      setMessage('Still waiting for approval.')
    }
  }

  const onApprove = async (device: UserDevice) => {
    setBusyId(device.deviceId)
    setError(null)
    setMessage(null)
    try {
      await approveMyDevice(device.deviceId, thisDeviceId)
      setMessage(`${device.displayName} approved.`)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not approve device')
    } finally {
      setBusyId(null)
    }
  }

  const onDeny = async (device: UserDevice) => {
    setError(null)
    setMessage(null)
    // Same step-up as removing a device on Manage devices.
    if (!(await requireStepUp('Confirm remove device'))) return
    setBusyId(device.deviceId)
    try {
      await revokeMyDevice(device.deviceId)
      setMessage(
        `${device.displayName} was removed. If you don’t recognise it, change your password.`,
      )
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove device')
    } finally {
      setBusyId(null)
    }
  }

  const pendingKey = summary.kind === 'this-pending' ? `${userId}:${summary.thisDevice.deviceId}` : null
  const onDismiss = () => {
    dismissedPendingKey = pendingKey
    setDismissedKey(pendingKey)
  }

  if (summary.kind === 'none') {
    return message ? <BannerShell tone="success">{message}</BannerShell> : null
  }

  if (summary.kind === 'this-pending') {
    if (pendingKey && dismissedKey === pendingKey) return null
    const approverNames = formatApproverNames(summary.approvers)
    const offerEmail = canVerifyByEmail(summary)
    const deviceNoun = summary.thisDevice.platform === 'web' ? 'browser' : 'device'
    return (
      <>
        <VerifyDeviceEmailDialog
          open={verifyOpen}
          deviceNoun={deviceNoun}
          onClose={() => setVerifyOpen(false)}
          onVerified={() => {
            setVerifyOpen(false)
            setMessage(`This ${deviceNoun} is now trusted.`)
            void refresh()
          }}
        />
        <BannerShell tone="warning">
          <div className="mx-auto flex max-w-3xl flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="font-semibold">
                This {summary.thisDevice.displayName} is waiting for approval
              </p>
              <p className="text-xs text-amber-800">
                {approverNames
                  ? `Approve it from Manage devices on your ${approverNames}.`
                  : offerEmail
                    ? `None of your trusted devices has been used in the last ${ACTIVE_APPROVER_WINDOW_DAYS} days, so verify this ${deviceNoun} by email instead.`
                    : 'Approve it from Manage devices on another device you use with Ascend.'}{' '}
                Calls and messages still work while you wait.{' '}
                <Link to="/account/devices" className="font-medium underline">
                  Manage devices
                </Link>
              </p>
              {message && <p className="mt-1 text-xs text-amber-900">{message}</p>}
              {error && (
                <p className="mt-1 text-xs text-red-700" role="alert">
                  {error}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {offerEmail && (
                <button
                  type="button"
                  onClick={() => setVerifyOpen(true)}
                  className="rounded-xl bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white outline-none hover:bg-amber-700 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
                >
                  Verify by email
                </button>
              )}
              <button
                type="button"
                disabled={checking}
                onClick={() => void onCheckAgain()}
                className="rounded-xl border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-900 outline-none hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-amber-500 disabled:opacity-50"
              >
                {checking ? 'Checking…' : 'Check again'}
              </button>
              <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss device approval notice"
                className="rounded-xl px-2 py-1.5 text-sm font-semibold text-amber-800 outline-none hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-amber-500"
              >
                Dismiss
              </button>
            </div>
          </div>
        </BannerShell>
      </>
    )
  }

  const { pending } = summary
  return (
    <BannerShell tone="warning">
      <StepUpConfirmModal state={guardState} onRetry={retryStepUp} onDismiss={dismissStepUp} />
      <div className="mx-auto flex max-w-3xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-semibold">
            {pending.length === 1
              ? `New device waiting: ${pending[0].displayName}`
              : `${pending.length} devices waiting for approval`}
          </p>
          <p className="text-xs text-amber-800">
            Approve only devices you recognize. “Not me” removes its access; it is signed out the
            next time it connects. If you don’t recognise it,{' '}
            <Link to="/account/login" className="font-medium underline">
              change your password
            </Link>
            .{' '}
            <Link to="/account/devices" className="font-medium underline">
              Manage devices
            </Link>
          </p>
          {message && <p className="mt-1 text-xs text-amber-900">{message}</p>}
          {error && (
            <p className="mt-1 text-xs text-red-700" role="alert">
              {error}
            </p>
          )}
        </div>
        {pending.length === 1 && (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={busyId === pending[0].deviceId}
              onClick={() => void onDeny(pending[0])}
              aria-label={`Refuse ${pending[0].displayName} — this is not me`}
              className="rounded-xl border border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 outline-none hover:bg-red-50 focus-visible:ring-2 focus-visible:ring-red-400 disabled:opacity-50"
            >
              Not me
            </button>
            <button
              type="button"
              disabled={busyId === pending[0].deviceId}
              onClick={() => void onApprove(pending[0])}
              aria-label={`Approve ${pending[0].displayName}`}
              className="rounded-xl bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white outline-none hover:bg-emerald-700 focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50"
            >
              Approve
            </button>
          </div>
        )}
      </div>
    </BannerShell>
  )
}

function BannerShell({ tone, children }: { tone: 'warning' | 'success'; children: ReactNode }) {
  const cls =
    tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : 'border-amber-200 bg-amber-50 text-amber-950'
  return (
    <div className={`border-b px-4 py-3 text-sm ${cls}`} role="status" aria-live="polite">
      {children}
    </div>
  )
}
